/* eslint-disable  */

import { Box } from '@mui/material';
import { Form, Spin } from 'antd';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import MainCard from 'components/MainCard';
import { toastConfig } from 'config';
import { isEqual } from 'lodash';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';

import CardAccordion from './CustomComponents/CardAccordion';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import { constantFormValues } from './root';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import { monthDiff, stateUpdater, utcConverter } from './CustomComponents/LeaseUtils/helper';
import AgreementFiles from './CustomComponents/cardSections/AgreementFiles';
import LeaseBulkUpload from './CustomComponents/LeaseBulkUpload2';
import GeneralLeaseInformation from './Components/TabItems/GeneralLeaseInformation';
import FinancialTermsConditions from './Components/TabItems/FinancialTermsConditions';
import IFRS16FieldsModifications from './Components/TabItems/IFRS16FieldsModifications';
import AccountingInformation from './CustomComponents/cardSections/AccountingInformation';
import LeasePageHeader from '../../console-solution/lease-schedule-v6/LeaseUtils/LeasePageHeader';
import LeaseHistory from './CustomComponents/LeaseUtils/History/LeaseHistory';
import LeaseSchedule from './CustomComponents/cardSections/LeaseSchedule';

const leaseDataCache = {};

dayjs.extend(utc);

const Create = ({ screen = 'CREATE' }) => {
	//constants
	dayjs.extend(utc);
	// Get cache key before component initializes
	const { state } = useLocation();
	const cacheKey = screen?.toLowerCase() === 'view' ? `${state?._id}-${state?.processCode}` : null;
	const initialCachedData = cacheKey ? leaseDataCache[cacheKey] : null;

	const [form] = Form.useForm();

	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const leaseDetailsShowAllRef = useRef();

	const [previousFormData, setPreviousFormData] = useState({});
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [selectedTab, setSelectedTab] = useState(0);
	// Combined States
	const [formState, setFormState] = useState({
		showFormSubmitModal: false,
		submitPayload: null,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});
	// Initialize dataFullyLoaded as true if we have cached data
	const [dataFullyLoaded, setDataFullyLoaded] = useState(!!initialCachedData);
	//simplified state setter
	const updateFormState = stateUpdater(setFormState);
	//Single States
	const [rowIndex, setRowIndex] = useState(0);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [creationApi, { isLoading: isCreatLoading, error: createError, isSuccess: isCreateSuccess, isError: isCreateError }] =
		useGetSingleIdentityMutation();

	// view Screen specific
	// Initialize with cached data if available
	const [cachedLeaseData, setCachedLeaseData] = useState(initialCachedData);

	const [
		getSingleLeaseData,
		{
			data: singleLeaseData, //viewData
			isLoading: isViewLoading,
		},
	] = useGetSingleIdentityMutation();
	// Use either cached data or API data
	const displayLeaseData = cachedLeaseData || singleLeaseData;

	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'view'
			? Object.keys(displayLeaseData?.parkedData ?? {})?.length === 0
				? displayLeaseData?.data
				: displayLeaseData?.parkedData
			: [];
	}, [displayLeaseData, screen]);
	// console.log('🚀 ~ statusData ~ statusData:', statusData);

	useEffect(() => {
		if (screen?.toLowerCase() === 'create') {
			form.resetFields();

			setTimeout(() => {
				if (initialFormValues && form) {
					form.setFieldsValue(initialFormValues);
					// For create mode, we need to set dataFullyLoaded to true after form initialization
					setDataFullyLoaded(true);
				}
			}, 0);
		}
		if (screen?.toLowerCase() === 'view') {
			// If we already have the cache key and cached data, no need to recalculate
			// We've already set the initial state with cached data
			if (!initialCachedData) {
				// If no cached data, fetch it
				const fetchLeaseData = async () => {
					try {
						const response = await getSingleLeaseData({
							suffixCode: '',
							_id: state?._id ?? '',
							operation: 'get-single',
							processCode: state?.processCode,
						}).unwrap();

						// Store in cache for future use
						leaseDataCache[cacheKey] = response;
						// No need to check for differences since we don't have initial cached data
						setCachedLeaseData(response);
					} catch (error) {
						console.error('Error fetching lease data:', error);
					}
				};
				fetchLeaseData();
			} else {
				// If we have cached data, still fetch in background to check for updates
				// But don't show any loading state
				const fetchInBackground = async () => {
					try {
						const response = await getSingleLeaseData({
							suffixCode: '',
							_id: state?._id ?? '',
							operation: 'get-single',
							processCode: state?.processCode,
						}).unwrap();

						// Only update if the data is actually different
						if (!isEqual(initialCachedData, response)) {
							leaseDataCache[cacheKey] = response;
							setCachedLeaseData(response);
						}
					} catch (error) {
						// Silent fail for background fetch
					}
				};
				// Use requestAnimationFrame to defer the background fetch until after rendering
				requestAnimationFrame(() => {
					fetchInBackground();
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [screen, initialCachedData, state?._id, state?.processCode, cacheKey]);
	useEffect(() => {
		if (screen === 'CREATE') {
			if (isCreateSuccess) {
				toast.success('Success! Action completed ', toastConfig);
				navigate(-1);
			}
			if (isCreateError) {
				if (createError?.data?.code === '501') {
					toast.error(createError?.data?.message, toastConfig);
				} else {
					toast.error('Failed! Action not completed', toastConfig);
				}
			}
		}
	}, [isCreateSuccess, createError, isCreateError, screen]);

	//constants

	const initialFormValues = useMemo(
		() => ({
			tabIndex: '1',
			version: '',
			retirementTerm: '',
			initialDiscountRate: '',
			retirementCost: '',
			changes: [
				{
					// inputChangeDescription: 'Initial Retirement', // Keep for API compatibility
					inputChangeDiscountRate: '',
					inputChangeRetirementCost: '',
					inputChangeRetirementTerm: '',
				},
			],
		}),
		[]
	);

	//states

	// Redux selectors
	const { creatorOrganizationId, userId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;

	useEffect(() => {
		if (screen?.toLowerCase() === 'view' && statusData) {
			const viewValues = {
				tabIndex: '1',
				retirementTerm: statusData?.retirementTerm,
				retirementStartDate: dayjs(statusData?.retirementStartDateField),
				// startYear: dayjs(statusData?.retirementStartDate).year(),
				// startMonth: dayjs(statusData?.retirementStartDate).month() + 1,
				// startDate: dayjs(statusData?.retirementStartDate).date(),
				initialDiscountRate: statusData?.initialDiscountRate,
				retirementCost: statusData?.retirementCost,
				// inputChangeMonth: statusData?.inputChangeMonth || '1',

				changes: statusData?.changes?.map((item, index) => {
					const { inputChangeEffectiveDate, ...change } = item;
					// console.log('🚀 ~ changes:statusData?.changes?.map ~ change:', change);
					return {
						// For first row, use retirementStartDate as effective date
						inputChangeEffectiveDate: index === 0 ? dayjs(statusData?.retirementStartDate) : dayjs(inputChangeEffectiveDate),
						// inputChangeDescription: change.inputChangeDescription || 'Initial Retirement', // Keep for API compatibility
						inputChangeDiscountRate: change.inputChangeDiscountRate,
						inputChangeRetirementCost: change.inputChangeRetirementCost,
						inputChangeRetirementTerm: change.inputChangeRetirementTerm,
						inputChangeRetirementDate: dayjs(change.inputChangeRetirementDate),
					};
				}),
			};

			setRowIndex(statusData?.changes?.length);

			form.setFieldsValue(viewValues);

			requestAnimationFrame(() => {
				// console.log('Setting dataFullyLoaded to true for view mode');
				setDataFullyLoaded(true);
			});
		}
	}, [screen, statusData, displayLeaseData]);

	useEffect(() => {
		// console.log('dataFullyLoaded changed:', dataFullyLoaded);
	}, [dataFullyLoaded]);

	const [selectHistory, setSelectHistory] = useState(null);

	// Handle form value changes to sync main fields with first row of changes
	const handleFormValuesChange = (changedValues, allValues) => {
		if (screen?.toLowerCase() === 'create') {
			let needsUpdate = false;
			const currentChanges = form.getFieldValue('changes') || [];
			if (currentChanges.length === 0) return;

			const updatedChanges = [...currentChanges];

			if (changedValues && changedValues.hasOwnProperty('retirementTerm')) {
				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeRetirementTerm: changedValues.retirementTerm || '',
				};
				needsUpdate = true;
			}

			if (changedValues && changedValues.hasOwnProperty('initialDiscountRate')) {
				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeDiscountRate: parseFloat(changedValues.initialDiscountRate) || 0,
				};
				needsUpdate = true;
			}

			if (changedValues && changedValues.hasOwnProperty('retirementCost')) {
				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeRetirementCost: parseFloat(changedValues.retirementCost) || 0,
				};
				needsUpdate = true;
			}

			if (changedValues && changedValues.hasOwnProperty('retirementStartDate')) {
				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeRetirementDate: changedValues.retirementStartDate,
				};
				needsUpdate = true;
			}

			if (needsUpdate) {
				form.setFieldValue('changes', updatedChanges);
			}
		}
	};

	const handleSubmit = async () => {
		try {
			const formData = form.getFieldsValue();
			const changes = formData?.changes || [];
			if (!changes.length) throw new Error('No changes found');

			// Get row 0's effective date
			const row0EffectiveDate = changes[0]?.inputChangeEffectiveDate || formData?.retirementStartDate;

			const processedChanges = changes.map((change, idx) => {
				const rowiEffectiveDate = change.inputChangeEffectiveDate || row0EffectiveDate;
				const rowiRetirementDate = change.inputChangeRetirementDate || formData?.retirementStartDate;
				const inputChangeMonth = Math.abs(monthDiff(row0EffectiveDate, rowiEffectiveDate)) + 1;
				const inputChangeRetirementTerm = Math.abs(monthDiff(row0EffectiveDate, rowiRetirementDate));
				return {
					...change,
					inputChangeMonth,
					inputChangeRetirementTerm,
					inputChangeEffectiveDate: utcConverter(rowiEffectiveDate),
					inputChangeRetirementDate: utcConverter(rowiRetirementDate),
				};
			});

			const createPagePayload = {
				retirementStartDate: utcConverter(processedChanges[0]?.inputChangeEffectiveDate),
				retirementStartDateField: utcConverter(formData?.retirementStartDate),
				retirementTerm: formData?.retirementTerm,
				initialDiscountRate: formData?.initialDiscountRate,
				retirementCost: formData?.retirementCost ?? 0,
				startYear: formData?.retirementStartDate ? dayjs(processedChanges[0]?.inputChangeEffectiveDate).year() : null,
				startMonth: formData?.retirementStartDate ? dayjs(processedChanges[0]?.inputChangeEffectiveDate).month() + 1 : null,
				startDate: formData?.retirementStartDate ? dayjs(processedChanges[0]?.inputChangeEffectiveDate).date() : null,
				inputChangeMonth: '1', // Default value for API
				changes: processedChanges,
			};

			updateFormState('showFormSubmitModal', true);
			updateFormState('modalData.head', 'Confirmation');
			updateFormState('modalData.label', 'Do you want to create?');
			updateFormState('submitPayload', createPagePayload);
			// console.log('🚀 ~ handleSubmit ~ createPagePayload:', createPagePayload);
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill all required fields', toastConfig);
		}
	};
	const handleModalSubmit = async () => {
		await creationApi({
			operation: 'add-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
		});
	};

	// MUI Tab handler
	const selectTabHandler = (event, newValue) => {
		console.log(
			'🚀 ~ Tab Change ~ screen:',
			screen?.toLowerCase(),
			'dataFullyLoaded:',
			dataFullyLoaded,
			'newValue:',
			newValue,
			'showLeaseSchedule:',
			newValue === 1 && dataFullyLoaded
		);
		if (isCreatLoading) return;
		setSelectedTab(newValue);
		if (newValue === 1 && dataFullyLoaded && screen?.toLowerCase() !== 'create') {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded && screen?.toLowerCase() === 'view') {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};
	const handleInputMonthChange = (e, index) => {
		const formData = form.getFieldsValue();

		if (index === 0) {
			if (!formData?.retirementStartDate) return;

			form.setFieldValue(constantFormValues?.retirementTerm?.key, Math?.abs(dayjs(e)?.diff(formData?.retirementStartDate, 'month')) + 1);
		}
		if (!e) {
			// Get the current changes list
			const currentChanges = form.getFieldValue('changes') || [];

			if (currentChanges[index]) {
				// Create a new array with cleared values
				const updatedChanges = [...currentChanges];
				updatedChanges[index] = {
					...updatedChanges[index],
					inputChangeMonth: null, // or 0, depending on your requirements
					inputChangeEffectiveDate: null,
					inputChangeRetirementTerm:
						dayjs(formData?.changes[index]?.inputChangeEffectiveDate).diff(
							dayjs(formData?.changes[index]?.inputChangeRetirementDate),
							'month'
						) + 1,
				};

				// Update the form
				form.setFieldsValue({ changes: updatedChanges });
			}
			return; // Exit early
		}
		const effectiveDate = dayjs(e);
		const leaseStart = dayjs(formData?.changes[0]?.inputChangeEffectiveDate);

		let monthDifference = (effectiveDate.year() - leaseStart.year()) * 12 + (effectiveDate.month() - leaseStart.month());

		monthDifference += 1;

		if (monthDifference < 0) {
			monthDifference = 0;
		}

		// Get the current changes list
		const currentChanges = form.getFieldValue('changes') || [];

		// Only update this specific row, don't modify the entire form
		// This prevents triggering a full form change detection
		if (currentChanges[index]) {
			// Create a new array with the updated values to avoid direct mutation
			const updatedChanges = [...currentChanges];
			updatedChanges[index] = {
				...updatedChanges[index],
				inputChangeMonth: monthDifference,
				inputChangeEffectiveDate: utcConverter(effectiveDate),
			};

			// Update the form in a single operation
			form.setFieldsValue({ changes: updatedChanges });
		}
	};

	// Only show loading if no cached data is available
	const showSpinner = screen?.toLowerCase() === 'view' ? isViewLoading && !initialCachedData : isCreatLoading;

	const tabItem = [
		{
			key: '1',
			label: 'Retirement Details',
			children: (
				<>
					<CardAccordion
						title="Basic Information"
						darkTitle={true}
						sx={{
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<GeneralLeaseInformation screen={screen} />
					</CardAccordion>

					<FinancialTermsConditions screen={screen} dataFullyLoaded={dataFullyLoaded} />
					<CardAccordion
						title="ARO Schedule"
						darkTitle={true}
						sx={{
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<IFRS16FieldsModifications screen={screen} rowIndex={rowIndex} handleInputMonthChange={handleInputMonthChange} />
					</CardAccordion>
					<ActionButtons
						screen={screen}
						viewData={singleLeaseData}
						selectHistory={setSelectHistory}
						dataFullyLoaded={dataFullyLoaded}
						ref={actionButtonsRef}
						handleCreation={handleSubmit}
					/>
				</>
			) || <></>,
		},
		{
			key: '2',
			label: 'Retirement Schedule',
			children: (
				<>
					<LeaseSchedule screen={screen} viewData={singleLeaseData} dataFullyLoaded={dataFullyLoaded} ref={leaseScheduleRef} />
				</>
			),
		},
		...(screen?.toLowerCase() === 'view'
			? [
					{
						key: '3',
						label: 'Modification History',
						children: (
							<>
								<LeaseHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
							</>
						),
					},
				]
			: []),
		...(screen?.toLowerCase() !== 'create'
			? [
					{
						key: screen?.toLowerCase() === 'view' ? '4' : '3',
						label: 'Accounts Assignment',
						children: (
							<>
								<AccountingInformation screen={screen} identityId={singleLeaseData?._id} />
							</>
						),
					},
				]
			: []),
		...(screen?.toLowerCase() === 'view'
			? [
					{
						key: screen?.toLowerCase() === 'view' ? '5' : '',
						label: 'General Ledgers',
						children: (
							<>
								<CardAccordion
									title="General Ledger View"
									darkTitle={true}
									sx={{
										mt: 2,
										mb: 2,
									}}
									customHeaderSX={{
										backgroundColor: '#e1f0fb !important',
										p: '10px',
										fontSize: '16px !important',
									}}
									typographySX={{ fontSize: '16px !important' }}
								>
									<LeaseDetailsShowAll ref={leaseDetailsShowAllRef} />
								</CardAccordion>
							</>
						),
					},
				]
			: []),
		...(screen?.toLowerCase() !== 'create' && statusData?.agreementFiles?.length > 0
			? [
					{
						key: screen?.toLowerCase() === 'view' ? '6' : '4',
						label: 'Agreements',
						children: (
							<>
								<CardAccordion
									title="Agreements"
									customHeaderSX={{
										backgroundColor: '#e1f0ff !important',
										p: '10px',
									}}
									typographySX={{ fontSize: '16px !important' }}
									darkTitle={true}
									sx={{ mb: 2 }}
								>
									<AgreementFiles screen={screen} />
								</CardAccordion>
							</>
						),
					},
				]
			: []),
		...(screen?.toLowerCase() !== 'view'
			? [
					{
						key: screen?.toLowerCase() === 'view' ? '7' : '5',
						label: 'Bulk Upload',
						children: <></>, // Empty children since we render it outside the form
					},
				]
			: []),
	];

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				<LeasePageHeader
					title={screen === 'CREATE' ? `Create ${state?.processName ?? ''}` : `View ${state?.processName ?? ''}`}
					tabsItem={tabItem}
					selectedTab={selectedTab}
					selectTabHandler={selectTabHandler}
					createBtn={false}
				/>

				<MainCard sx={{ overflow: 'visible' }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						requiredMark={false}
						initialValues={initialFormValues}
						onValuesChange={handleFormValuesChange}
					>
						<AntdValuePreview form={form} show={true} />
						<Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<Box
									key={index}
									style={{
										display: selectedTab === index ? 'block' : 'none',
									}}
								>
									{/* Only render Bulk Upload outside of form */}
									{item.key === (screen?.toLowerCase() === 'view' ? '7' : '5') ? null : item.children}
								</Box>
							))}
						</Box>
					</Form>

					{/* Render Bulk Upload outside of form to prevent nested forms */}
					{selectedTab === 2 && screen?.toLowerCase() !== 'view' && (
						<Box sx={{ mt: 2 }}>
							<LeaseBulkUpload />
						</Box>
					)}
				</MainCard>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						showModal={formState?.showFormSubmitModal}
						modalData={formState?.modalData}
						setShowModal={setShowFormSubmitModal}
						handleSubmit={handleModalSubmit}
					/>
				)}
				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						setSelectHistory={setSelectHistory}
						selectItem={selectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default Create;
